import { getFinalCost } from "domain/quote/pricing";

import { useEffect, useRef, useState } from "react";

import QuotationTable from "components/QuotationTable";
import ShopifyOrderButton from "components/ShopifyOrderButton";
import { useOptionContext } from "context/OptionContext";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "firebaseConfig";
import i18n from "i18";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
  applyAttachmentInclusionRule,
  applyAttachmentAdjustments,
  calculateSummaryTotal,
  renderQuoteToCanvas,
} from "services/quoteRuntime";
import {
  Attachment,
  AttachmentAdjustments,
  FinalCost,
  Line,
  Material,
  MaterialType,
  Point,
  Province,
  QuotationFireStoreFields,
} from "types/canvasTypes";

import "react-toastify/dist/ReactToastify.css";

type ClientDetails = {
  name: string;
  email: string;
  phone: string;
  address: string;
  note?: string;
  chalkLine: boolean;
  gapSize?: number;
};

const AdminQuotationDetails = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lines, setLines] = useState<Line[]>([]);
  const [clientDetails, setClientDetails] = useState<ClientDetails>();

  const [date, setDate] = useState<string>("");
  const [finalCost, setFinalCost] = useState<FinalCost[]>([]);
  const [material, setMaterial] = useState<Attachment[]>([]);
  const [totalCost, setTotalCost] = useState<number>(0);
  const [scale, setScale] = useState<number>(1);
  const [selectedLength, setSelectedLength] = useState<string>("42");
  const [province, setProvince] = useState<Province>(Province.Quebec);
  const [materialOption, setMaterialOption] = useState<MaterialType>(
    MaterialType.Silver
  );
  const [includeAttachement, setIncludeAttachement] = useState<boolean>(false);
  const [pannels, setPannels] = useState<any[]>([]);
  const [attachmentAdjustments, setAttachmentAdjustments] =
    useState<AttachmentAdjustments>({});
  const [viewportZoom, setViewportZoom] = useState<number>(1);
  const [viewportOffset, setViewportOffset] = useState<Point>({
    x: 0,
    y: 0,
  });
  const [isPanning, setIsPanning] = useState(false);
  const panStartPointRef = useRef<Point | null>(null);
  const panStartOffsetRef = useRef<Point>({ x: 0, y: 0 });

  const {
    setLines: setContextLines,
    setHeight,
    setScale: setContextScale,
    setMaterialType,
  } = useOptionContext();

  const [lastApiCall, setLastApiCall] = useState<number>(0);
  const MIN_API_CALL_INTERVAL = 2000;
  const MIN_VIEWPORT_ZOOM = 0.5;
  const MAX_VIEWPORT_ZOOM = 3;
  const VIEWPORT_ZOOM_STEP = 0.1;

  const debouncedApiCall = async (
    apiFunction: () => Promise<any>,
    storeName: string
  ) => {
    const now = Date.now();
    const timeSinceLastCall = now - lastApiCall;

    if (timeSinceLastCall < MIN_API_CALL_INTERVAL) {
      const waitTime = MIN_API_CALL_INTERVAL - timeSinceLastCall;
      toast.info(
        `Please wait ${Math.ceil(waitTime / 1000)} seconds before making another request`
      );
      return;
    }

    setLastApiCall(now);
    setLoading(true);

    try {
      const result = await apiFunction();
      console.log("Draft Order:", result);
      toast.success(`Success! Sent to ${storeName}`);
    } catch (err) {
      console.log("Error:", err);
      toast.error(`Error sending to ${storeName}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!slug) return;
      try {
        const quotationRef = doc(db, "quotations", slug);
        const quotationDoc = await getDoc(quotationRef);
        if (!quotationDoc.exists()) return;

        const quotation = quotationDoc.data() as QuotationFireStoreFields;
        setLines(JSON.parse(quotation.lines));
        setClientDetails({
          name: quotation?.name || "",
          email: quotation?.email || "",
          phone: quotation?.phone || "",
          address: quotation?.address || "",
          note: quotation?.note || "",
          chalkLine: quotation?.chalkLine || false,
          gapSize: quotation?.gapSize || 1,
        });
        const milliseconds =
          quotation.date.seconds * 1000 +
          quotation.date.nanoseconds / 1000000;
        setDate(new Date(milliseconds).toLocaleDateString("fr-FR"));

        if (quotation.material === "Stainless") {
          setMaterialOption(MaterialType.Silver);
        } else {
          setMaterialOption(MaterialType.Black);
        }
        if (quotation.selectedLength) setSelectedLength(quotation.selectedLength);
        if (quotation.scale) setScale(quotation.scale);
        setIncludeAttachement(Boolean(quotation.includeAttachement));
        setAttachmentAdjustments(quotation.attachmentAdjustments || {});
        if (quotation.province) {
          if ((quotation.province as Province) === Province.Ontario) {
            i18n.changeLanguage("en");
          } else {
            i18n.changeLanguage("fr");
          }
          setProvince(quotation.province as Province);
        }
      } catch (error) {
        console.error(error);
      }
    };
    fetchData();
  }, [slug]);

  useEffect(() => {
    setViewportZoom(1);
    setViewportOffset({ x: 0, y: 0 });
  }, [slug]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || lines.length === 0) return;
    const context = canvas.getContext("2d");

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const desiredWidth = Math.min(screenWidth * 0.7, 800);
    const desiredHeight = Math.min(screenHeight * 0.5, 600);
    canvas.width = desiredWidth;
    canvas.height = desiredHeight;

    if (!context) return;
    const { pannels: newPannels, material: newMaterial } = renderQuoteToCanvas({
      context,
      canvas,
      lines,
      glassHeight: parseInt(selectedLength),
      scale,
      chalkLine: clientDetails?.chalkLine || false,
      gapSize: clientDetails?.gapSize || 1,
      viewportZoom,
      viewportOffset,
    });

    setPannels(newPannels);

    const { finalCost, attachment } = getFinalCost(
      newPannels,
      materialOption,
      newMaterial,
      includeAttachement,
      lines.filter((line) => line.numberOfDoors > 1).length > 0,
      province
    );

    const filteredAttachment = applyAttachmentInclusionRule(
      attachment,
      includeAttachement
    );
    const adjustedAttachment = applyAttachmentAdjustments(
      filteredAttachment,
      attachmentAdjustments
    );

    setFinalCost(finalCost);
    setMaterial(adjustedAttachment);
    setTotalCost(
      calculateSummaryTotal(
        finalCost,
        adjustedAttachment,
        materialOption,
        province
      )
    );
  }, [
    attachmentAdjustments,
    lines,
    clientDetails?.chalkLine,
    clientDetails?.gapSize,
    includeAttachement,
    materialOption,
    province,
    scale,
    selectedLength,
    viewportZoom,
    viewportOffset,
  ]);

  const clampViewportZoom = (value: number) => {
    return Math.min(Math.max(value, MIN_VIEWPORT_ZOOM), MAX_VIEWPORT_ZOOM);
  };

  const getCanvasPointFromEvent = (
    event: React.MouseEvent<HTMLCanvasElement>
  ): Point | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();

    return {
      x: ((event.clientX - rect.left) * canvas.width) / rect.width,
      y: ((event.clientY - rect.top) * canvas.height) / rect.height,
    };
  };

  const handleCanvasWheel = (event: React.WheelEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const canvasPoint = getCanvasPointFromEvent(event);
    if (!canvasPoint) return;
    const zoomDirection = event.deltaY < 0 ? 1 : -1;
    const nextZoom = clampViewportZoom(
      viewportZoom + zoomDirection * VIEWPORT_ZOOM_STEP
    );

    if (nextZoom === viewportZoom) return;

    const worldX = (canvasPoint.x - viewportOffset.x) / viewportZoom;
    const worldY = (canvasPoint.y - viewportOffset.y) / viewportZoom;

    setViewportZoom(nextZoom);
    setViewportOffset({
      x: canvasPoint.x - worldX * nextZoom,
      y: canvasPoint.y - worldY * nextZoom,
    });
  };

  const handleCanvasMouseDown = (
    event: React.MouseEvent<HTMLCanvasElement>
  ) => {
    if (event.button !== 0) return;
    const canvasPoint = getCanvasPointFromEvent(event);
    if (!canvasPoint) return;

    setIsPanning(true);
    panStartPointRef.current = canvasPoint;
    panStartOffsetRef.current = { ...viewportOffset };
  };

  const handleCanvasMouseMove = (
    event: React.MouseEvent<HTMLCanvasElement>
  ) => {
    if (!isPanning || !panStartPointRef.current) return;
    const canvasPoint = getCanvasPointFromEvent(event);
    if (!canvasPoint) return;

    const deltaX = canvasPoint.x - panStartPointRef.current.x;
    const deltaY = canvasPoint.y - panStartPointRef.current.y;
    setViewportOffset({
      x: panStartOffsetRef.current.x + deltaX,
      y: panStartOffsetRef.current.y + deltaY,
    });
  };

  const stopPanning = () => {
    setIsPanning(false);
    panStartPointRef.current = null;
  };

  const handleZoomIn = () => {
    setViewportZoom((currentZoom) =>
      clampViewportZoom(currentZoom + VIEWPORT_ZOOM_STEP)
    );
  };

  const handleZoomOut = () => {
    setViewportZoom((currentZoom) =>
      clampViewportZoom(currentZoom - VIEWPORT_ZOOM_STEP)
    );
  };

  const handleResetCanvasZoom = () => {
    setViewportZoom(1);
    setViewportOffset({ x: 0, y: 0 });
  };

  const handlePrint = () => window.print();

  const handleView3D = () => {
    setContextLines(lines);
    setHeight(parseInt(selectedLength) as 36 | 42 | 48);
    const validScale = scale === 1 || scale === 1.5 || scale === 2.9 ? scale : 2.9;
    setContextScale(validScale as 2.9 | 1.5 | 1);
    setMaterialType(materialOption);
    navigate(`/3d-visualization?returnTo=/admin/quotations/${slug}`);
  };

  const handleAttachmentCountChange = (
    attachmentMaterial: Material,
    delta: number
  ) => {
    setAttachmentAdjustments((previousAdjustments) => {
      const currentCount =
        material.find((item) => item.material === attachmentMaterial)?.count || 0;

      if (delta < 0 && currentCount <= 0) {
        return previousAdjustments;
      }

      const nextAdjustments = {
        ...previousAdjustments,
        [attachmentMaterial]:
          (previousAdjustments[attachmentMaterial] || 0) + delta,
      };

      if (slug) {
        void updateDoc(doc(db, "quotations", slug), {
          attachmentAdjustments: nextAdjustments,
        }).catch((error) => {
          console.error("Failed to persist attachment adjustments", error);
        });
      }

      return nextAdjustments;
    });
  };

  return (
    <div className="admin-quotation-detail">
      <div className="admin-quotation-detail__header">
        <div>
          <h1>Quotation #{slug?.slice(0, 8)}</h1>
          <span className="admin-quotation-detail__date">{date}</span>
        </div>
        <div className="admin-quotation-detail__actions">
          <button onClick={handlePrint}>Print</button>
          <button
            className="btn-3d"
            onClick={handleView3D}
            disabled={lines.length === 0}
          >
            View 3D
          </button>
          {province === Province.Quebec ? (
            <ShopifyOrderButton
              color="green"
              storeId="bc"
              storeName="Main Store"
              label="Send to BC"
              slug={slug || ""}
              clientName={clientDetails?.name || ""}
              clientEmail={clientDetails?.email || ""}
              includeAttachement={includeAttachement}
              materialOption={materialOption}
              selectedLength={selectedLength}
              pannels={pannels}
              material={material}
              loading={loading}
              onApiCall={debouncedApiCall}
            />
          ) : (
            <>
              <ShopifyOrderButton
                color="blue"
                storeId="gr"
                storeName="Glass Railing Store"
                label="Send to GR"
                slug={slug || ""}
                clientName={clientDetails?.name || ""}
                clientEmail={clientDetails?.email || ""}
                includeAttachement={includeAttachement}
                materialOption={materialOption}
                selectedLength={selectedLength}
                pannels={pannels}
                material={material}
                loading={loading}
                onApiCall={debouncedApiCall}
              />
              <ShopifyOrderButton
                color="purple"
                storeId="rf"
                storeName="RF Transparent Store"
                label="Send to RF"
                slug={slug || ""}
                clientName={clientDetails?.name || ""}
                clientEmail={clientDetails?.email || ""}
                includeAttachement={includeAttachement}
                materialOption={materialOption}
                selectedLength={selectedLength}
                pannels={pannels}
                material={material}
                loading={loading}
                onApiCall={debouncedApiCall}
              />
            </>
          )}
        </div>
      </div>

      <div className="admin-quotation-detail__grid">
        <div className="admin-card">
          <h3 className="admin-card__title">Client Information</h3>
          <dl className="admin-detail-list">
            <dt>Name</dt>
            <dd>{clientDetails?.name || "---"}</dd>
            <dt>Email</dt>
            <dd>{clientDetails?.email?.toLowerCase() || "---"}</dd>
            <dt>Phone</dt>
            <dd>{clientDetails?.phone || "---"}</dd>
            <dt>Address</dt>
            <dd>{clientDetails?.address || "---"}</dd>
            {clientDetails?.note && (
              <>
                <dt>Note</dt>
                <dd>{clientDetails.note}</dd>
              </>
            )}
          </dl>
        </div>

        <div className="admin-card">
          <h3 className="admin-card__title">Configuration</h3>
          <dl className="admin-detail-list">
            <dt>Province</dt>
            <dd>{province}</dd>
            <dt>Height</dt>
            <dd>{selectedLength}"</dd>
            <dt>Material</dt>
            <dd>{materialOption}</dd>
            <dt>Chalk Line</dt>
            <dd>{clientDetails?.chalkLine ? "Yes" : "No"}</dd>
            {clientDetails?.gapSize !== 1 && (
              <>
                <dt>Gap Size</dt>
                <dd>{clientDetails?.gapSize}"</dd>
              </>
            )}
          </dl>
        </div>
      </div>

      <div className="admin-card">
        <div className="admin-quotation-detail__canvas-header">
          <h3 className="admin-card__title">Layout Preview</h3>
          <div className="admin-quotation-detail__canvas-controls">
            <button
              type="button"
              onClick={handleZoomOut}
              disabled={viewportZoom <= MIN_VIEWPORT_ZOOM}
              aria-label="Zoom out"
            >
              -
            </button>
            <button
              type="button"
              onClick={handleResetCanvasZoom}
              aria-label="Reset zoom"
            >
              {Math.round(viewportZoom * 100)}%
            </button>
            <button
              type="button"
              onClick={handleZoomIn}
              disabled={viewportZoom >= MAX_VIEWPORT_ZOOM}
              aria-label="Zoom in"
            >
              +
            </button>
            <button type="button" onClick={handleResetCanvasZoom}>
              Center
            </button>
          </div>
        </div>
        <div className="admin-quotation-detail__canvas-wrap">
          <canvas
            className="canvas-element"
            ref={canvasRef}
            id="canvas"
            height="600"
            width="600"
            onWheel={handleCanvasWheel}
            onMouseDown={handleCanvasMouseDown}
            onMouseMove={handleCanvasMouseMove}
            onMouseUp={stopPanning}
            onMouseLeave={stopPanning}
            style={{ cursor: isPanning ? "grabbing" : "grab" }}
          />
        </div>
      </div>

      <div className="admin-card">
        <h3 className="admin-card__title">Cost Breakdown</h3>
        <QuotationTable
          province={province}
          selectedLength={parseInt(selectedLength)}
          finalCost={finalCost}
          materialOption={materialOption}
          material={material}
          totalCost={totalCost}
          editableAttachments={true}
          onAttachmentCountChange={handleAttachmentCountChange}
        />
      </div>

      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default AdminQuotationDetails;
